import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "sonner";
import {
  Box,
  Checkbox,
  InputAdornment,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  ListItemText,
  Skeleton,
  IconButton,
} from "@mui/material";

import { BlankPreview } from "../../components/BlankPreview";
import nopreview from "../../assets/images/others/no-preview.png";
import { useGetPatientsList, toggleStarPatient } from "../../api/collection";
import { useGetCentres } from "../../api/settings";

const PAGE_SIZE = 10;

const getPatientName = (patient) =>
  [patient?.firstName, patient?.lastName].filter(Boolean).join(" ") || "--";

const formatLastVisit = (timestamp) => {
  if (!timestamp) return "--";
  const date = new Date(Number(timestamp));
  return isNaN(date.getTime())
    ? "--"
    : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const columns = [
  { key: "name", label: "Patient Name", sortable: true },
  { key: "gender", label: "Gender", sortable: false },
  { key: "age", label: "Age", sortable: true },
  { key: "totalEcg", label: "Total ECG", sortable: false },
  { key: "lastVisit", label: "Last ECG", sortable: true },
  { key: "star", label: "", sortable: false },
];

function PatientsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0); // 0-indexed internally, sent as page+1 to the API
  const [limit] = useState(PAGE_SIZE);
  const [searchText, setSearchText] = useState("");
  // Most-recent-first by default, matching the backend's default (see
  // collectionController.js's getPatientsList) - the list should open showing
  // whoever staff most likely just saw, not alphabetically.
  const [sortBy, setSortBy] = useState("lastVisit");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedCenterIds, setSelectedCenterIds] = useState([]);
  const [starredOnly, setStarredOnly] = useState(false);
  // Optimistic overrides so the star reflects the click immediately, keyed by
  // patient _id - the underlying SWR cache only catches up on next refetch.
  const [starOverrides, setStarOverrides] = useState({});

  const { centres } = useGetCentres({ limit: 200 });

  const { patients: rawPatients, pagination, isLoading, error } = useGetPatientsList({
    page: page + 1,
    limit,
    searchText: searchText || undefined,
    sortBy,
    sortDir,
    centerIds: selectedCenterIds.length ? JSON.stringify(selectedCenterIds) : undefined,
    starred: starredOnly || undefined,
  });

  const patients = rawPatients.map((patient) =>
    patient._id in starOverrides ? { ...patient, starred: starOverrides[patient._id] } : patient
  );

  const handleToggleStar = async (patient) => {
    const nextStarred = !patient.starred;
    setStarOverrides((current) => ({ ...current, [patient._id]: nextStarred }));

    const response = await toggleStarPatient(patient._id, nextStarred);
    if (!response?.success) {
      setStarOverrides((current) => ({ ...current, [patient._id]: patient.starred }));
      toast.error(response?.message || "Unable to update star. Please try again.");
    }
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(0);
  };

  const showEmpty = !isLoading && !error && patients.length === 0;
  const total = pagination.total || 0;
  const from = total === 0 ? 0 : page * limit + 1;
  const to = Math.min((page + 1) * limit, total);

  return (
    <>
      <Helmet>
        <title>Spandan - Patients</title>
      </Helmet>
      <div className="main-wrappers">
        <div
          className="page-wrapper"
          style={{
            maxHeight: "99vh",
            overflowY: "auto",
            paddingBottom: 10,
            fontFamily: "Figtree",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <div className="content">
            <Box
              sx={{
                border: "1px solid var(--app-border)",
                borderRadius: "14px",
                backgroundColor: "var(--app-surface)",
                p: 2.5,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search by patient ID, name, phone number or ABDM ID"
                onChange={(event) => handleSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 45,
                    borderRadius: "999px",
                    backgroundColor: "var(--app-bg)",
                    "& fieldset": { borderColor: "var(--app-border)" },
                  },
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
                <Select
                  multiple
                  displayEmpty
                  value={selectedCenterIds}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedCenterIds(typeof value === "string" ? value.split(",") : value);
                    setPage(0);
                  }}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? "All Centres"
                      : `${selected.length} Centre${selected.length > 1 ? "s" : ""}`
                  }
                  IconComponent={(props) => (
                    <Icon icon="eva:arrow-ios-downward-fill" width="16" height="16" {...props} />
                  )}
                  sx={{
                    height: 40,
                    borderRadius: "999px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--app-text)",
                    backgroundColor: "var(--app-bg)",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--app-border)" },
                    "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0, pr: 4 },
                  }}
                >
                  {centres.map((centre) => (
                    <MenuItem key={centre.id} value={centre.id} dense>
                      <Checkbox size="small" checked={selectedCenterIds.indexOf(centre.id) > -1} />
                      <ListItemText primary={centre.name} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                </Select>

                <Box
                  onClick={() => {
                    setStarredOnly((current) => !current);
                    setPage(0);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    height: 40,
                    px: 1.75,
                    borderRadius: "999px",
                    border: "1px solid var(--app-border)",
                    backgroundColor: starredOnly ? "var(--app-accent-soft)" : "var(--app-bg)",
                    color: starredOnly ? "var(--app-accent)" : "var(--app-text)",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <Icon icon={starredOnly ? "mdi:star" : "mdi:star-outline"} width="16" height="16" />
                  Starred
                </Box>
              </Box>

              <Typography sx={{ fontFamily: "Figtree", fontSize: 20, fontWeight: 700, mt: 3, mb: 1.5 }}>
                Patients{" "}
                <Box component="span" sx={{ color: "var(--app-text-muted)", fontSize: 14, fontWeight: 700 }}>
                  ({total})
                </Box>
              </Typography>

              {error ? (
                <BlankPreview
                  sx={{ minHeight: "50vh" }}
                  icon={nopreview}
                  title="Unable to load patients"
                  subtitle="Please try again in a moment."
                />
              ) : showEmpty ? (
                <BlankPreview sx={{ minHeight: "50vh" }} icon={nopreview} title="No Patients Found" />
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: "60vh", overflow: "auto" }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 700 }} aria-label="Patients">
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => (
                            <TableCell
                              key={column.key}
                              sx={{
                                backgroundColor: "var(--app-surface)",
                                color: "var(--app-text-muted)",
                                fontFamily: "Figtree",
                                fontSize: 13,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                borderBottom: "1px solid var(--app-border)",
                                py: 1.5,
                              }}
                            >
                              {column.sortable ? (
                                <Box
                                  onClick={() => handleSort(column.key)}
                                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                                >
                                  {column.label}
                                  <Icon
                                    icon="mdi:swap-vertical"
                                    width="15"
                                    height="15"
                                    style={{
                                      color: sortBy === column.key ? "var(--app-accent)" : "var(--app-text-muted)",
                                    }}
                                  />
                                </Box>
                              ) : (
                                column.label
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoading
                          ? [1, 2, 3, 4, 5].map((row) => (
                              <TableRow key={row}>
                                <TableCell colSpan={columns.length}>
                                  <Skeleton variant="rounded" height={40} animation="wave" />
                                </TableCell>
                              </TableRow>
                            ))
                          : patients.map((patient) => (
                              <TableRow
                                key={patient._id}
                                hover
                                onClick={() => navigate(`/patients/${patient._id}`)}
                                sx={{
                                  cursor: "pointer",
                                  "& td": { borderBottom: "1px solid var(--app-border)", py: 1.6 },
                                }}
                              >
                                <TableCell>
                                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                    {getPatientName(patient)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)" }}>
                                    {patient.gender || "--"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)" }}>
                                    {patient.age ?? "--"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)" }}>
                                    {patient.totalEcg ?? 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)" }}>
                                    {formatLastVisit(patient.lastVisit)}
                                  </Typography>
                                </TableCell>
                                <TableCell onClick={(event) => event.stopPropagation()} align="right">
                                  <IconButton
                                    size="small"
                                    aria-label={patient.starred ? "Unstar patient" : "Star patient"}
                                    onClick={() => handleToggleStar(patient)}
                                  >
                                    <Icon
                                      icon={patient.starred ? "mdi:star" : "mdi:star-outline"}
                                      width="20"
                                      height="20"
                                      style={{ color: patient.starred ? "var(--app-accent)" : "var(--app-text-muted)" }}
                                    />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                    <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)" }}>
                      Showing {from}-{to} of {total}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage((current) => current - 1)}
                        sx={{ border: "1px solid var(--app-border)", borderRadius: "50%" }}
                      >
                        <Icon icon="mdi:chevron-left" width="18" height="18" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={to >= total}
                        onClick={() => setPage((current) => current + 1)}
                        sx={{ border: "1px solid var(--app-border)", borderRadius: "50%" }}
                      >
                        <Icon icon="mdi:chevron-right" width="18" height="18" />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </div>
        </div>
      </div>
    </>
  );
}

export default PatientsList;
